import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// ---------- Response interfaces ----------

export interface VendorStatusItem {
  id: number;
  nameStatus: string;
  labelForStatus: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
}

export interface VendorRoleWithStatuses {
  id: number;
  role_name: string;
  role_description: string | null;
  org_id: number;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  statuses: VendorStatusItem[];
}

export interface UserWithVendorRole {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  username: string | null;
  vendorRole: { id: number; role_name: string } | null;
  assignmentId: number | null;
}

export interface OrganizationItem {
  id: number;
  org_name: string;
}

// ---------- Service ----------

@Injectable({
  providedIn: 'root',
})
export class VendorRolesService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // -- Vendor Roles with statuses (enriched) --

  getVendorRolesWithStatuses(orgId: number): Observable<{
    data: VendorRoleWithStatuses[];
    allStatuses: VendorStatusItem[];
  }> {
    const params = new HttpParams().set('org_id', orgId.toString());
    return this.http
      .get<{
        status: string;
        data: VendorRoleWithStatuses[];
        allStatuses: VendorStatusItem[];
      }>(`${this.apiUrl}v1/vendor/vendor-roles/with-statuses`, { params })
      .pipe(map((res) => ({ data: res.data, allStatuses: res.allStatuses })));
  }

  createVendorRole(data: {
    role_name: string;
    role_description?: string;
    org_id: number;
    is_active?: boolean;
  }): Observable<VendorRoleWithStatuses> {
    return this.http
      .post<{ status: string; data: VendorRoleWithStatuses }>(
        `${this.apiUrl}v1/vendor/vendor-roles`,
        data,
      )
      .pipe(map((res) => res.data));
  }

  updateVendorRole(
    roleId: number,
    orgId: number,
    data: { role_name?: string; role_description?: string; is_active?: boolean },
  ): Observable<VendorRoleWithStatuses> {
    const params = new HttpParams().set('org_id', orgId.toString());
    return this.http
      .put<{ status: string; data: VendorRoleWithStatuses }>(
        `${this.apiUrl}v1/vendor/vendor-roles/${roleId}`,
        data,
        { params },
      )
      .pipe(map((res) => res.data));
  }

  deleteVendorRole(roleId: number, orgId: number): Observable<void> {
    const params = new HttpParams().set('org_id', orgId.toString());
    return this.http
      .delete<void>(`${this.apiUrl}v1/vendor/vendor-roles/${roleId}`, { params });
  }

  // -- Bulk status update for a role --

  updateRoleStatuses(roleId: number, statusIds: number[]): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}v1/vendor/vendor-roles/${roleId}/statuses`,
      { status_ids: statusIds },
    );
  }

  // -- All statuses --

  getAllStatuses(): Observable<VendorStatusItem[]> {
    return this.http
      .get<{ status: string; data: VendorStatusItem[] }>(
        `${this.apiUrl}v1/vendor/vendor-status`,
      )
      .pipe(map((res) => res.data));
  }

  // -- Users by org with vendor role --

  getUsersByOrg(orgId: number): Observable<UserWithVendorRole[]> {
    const params = new HttpParams().set('org_id', orgId.toString());
    return this.http
      .get<{ status: string; data: UserWithVendorRole[] }>(
        `${this.apiUrl}v1/vendor/users-vendor-role/by-org`,
        { params },
      )
      .pipe(map((res) => res.data));
  }

  // -- Assign / remove vendor role --

  assignVendorRole(userId: number, vendorRoleId: number): Observable<{ id: number }> {
    return this.http
      .post<{ status: string; data: { id: number } }>(
        `${this.apiUrl}v1/vendor/users-vendor-role`,
        { user_id: userId, vendor_role_id: vendorRoleId },
      )
      .pipe(map((res) => res.data));
  }

  updateVendorRoleAssignment(
    assignmentId: number,
    vendorRoleId: number,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}v1/vendor/users-vendor-role/${assignmentId}`,
      { vendor_role_id: vendorRoleId },
    );
  }

  removeVendorRole(assignmentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}v1/vendor/users-vendor-role/${assignmentId}`,
    );
  }

  // -- Organizations --

  getOrganizations(): Observable<OrganizationItem[]> {
    return this.http
      .get<{ data: OrganizationItem[] }>(`${this.apiUrl}organizations`)
      .pipe(map((res) => res.data));
  }
}
